import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Avatar, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Typography } from '@mui/material';
import toast from 'react-hot-toast';

import { useDecodedToken } from 'src/hooks/useDecodedToken';
import { queryApi } from 'src/api/queryApi';
import { useDispatch } from 'react-redux';
import { addToQueue } from 'src/store/slices/queueSlice';

const ClaimButton = ({ query, claimedUser, mutate = null, sx, handleDiscardQuery = null, hideAvatar = false }) => {
  const { decodedToken, isExpert } = useDecodedToken();
  const dispatch = useDispatch();
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const hasClaimed = decodedToken?.userID === query?.userClaimID;

  const handleClaimToggle = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const isCurrentlyClaimed = hasClaimed;
    const newClaimID = isCurrentlyClaimed ? null : decodedToken?.userID;
    const action = isCurrentlyClaimed ? 'Unclaim' : 'Claim';
    const status = query?.status === 'new' || query?.status === 'discarded' ? 'draft' : null;

    setOpenConfirmDialog(false);

    try {
      if (query?.status === 'new') {
        dispatch(addToQueue({ queryID: Number(query.id) }));
      }

      const newStatus = status || query.status;

      const response = await queryApi.updateQuery(query.id, newStatus, newClaimID);
      console.log('response', response);
      if (response.status === 'Query Updated') {
        toast.success(`Query ${action.toLowerCase()}ed successfully`);
        if (mutate) {
          mutate((queries) => {
            return queries.map((q) => (q.id === query.id ? { ...q, status: status || q.status, userClaimID: newClaimID } : q));
          }, false);
        }
      } else {
        throw new Error(`Failed to ${action.toLowerCase()} query`);
      }
    } catch (error) {
      toast.error('Error updating query:', error);
      if (mutate) mutate();
    }
  };

  const handleClaimClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (query.userClaimID === null) {
      await handleClaimToggle(event);
      return;
    }

    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = (event) => {
    event.stopPropagation();
    setOpenConfirmDialog(false);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          ...sx,
        }}
      >
        {!hideAvatar && (
          <Avatar src={claimedUser?.avatar} alt={claimedUser?.firstName}>
            {claimedUser?.firstName?.charAt(0)}
          </Avatar>
        )}
        {isExpert && (
          <Typography
            variant="body1"
            sx={{
              ml: hideAvatar ? 0 : 2,
              mr: 0,
              width: '100%',
            }}
          >
            {claimedUser?.firstName}
          </Typography>
        )}
        {!isExpert && (
          <Button variant="contained" onClick={handleClaimClick} sx={{ ml: hideAvatar ? 0 : 2, mr: 0, width: '100%' }}>
            {hasClaimed ? 'Unclaim' : 'Claim'}
          </Button>
        )}
        {handleDiscardQuery && (
          <Button variant="contained" color="inherit" onClick={() => handleDiscardQuery()} sx={{ ml: 2, width: '100%' }}>
            Discard
          </Button>
        )}
      </Box>
      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Action</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">Are you sure you want to {hasClaimed ? 'unclaim' : 'claim'} this query?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={(event) => handleClaimToggle(event, query.status === 'new' ? 'draft' : query.status)}
            color="primary"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ClaimButton.propTypes = {
  query: PropTypes.shape({
    id: PropTypes.number.isRequired,
    status: PropTypes.string.isRequired,
    userClaimID: PropTypes.number,
  }).isRequired,
  claimedUser: PropTypes.shape({
    avatar: PropTypes.string,
    firstName: PropTypes.string,
  }),
  mutate: PropTypes.func,
  sx: PropTypes.object,
  handleDiscardQuery: PropTypes.func,
  hideAvatar: PropTypes.bool,
};

export default ClaimButton;
